import { ReactNode, useContext } from 'react';
import { t } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import cn from 'classnames';

import Button from '@/design_system/Button';
import PhotoCard from '@/design_system/PhotoCard';
import { PhotoCardProps } from '@/design_system/PhotoCard/PhotoCard';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconError from '@/icons/Error.svg';
import IconPlace from '@/icons/Place.svg';
import IconRefusal from '@/icons/Refusal.svg';
import IconValidation from '@/icons/Validation.svg';
import {
  computeWarrantyExpirationDate,
  getArticleDisplayPhoto,
  isMissingCustomsData,
  useArticleName,
} from '@/models/article';
import { ArticleWithRelations as ArticleWithRelationsFromRequest } from '@/models/request';
import { ArticleWithRelations as ArticleWithRelationsFromShipment } from '@/models/shipment';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { ShipmentArticlesContext } from '@/routes/Shipments/Shipment/components/ShipmentArticles/ShipmentArticles';
import { useDatabaseTranslation } from '@/services/i18n';
import { formatCurrency } from '@/utils/number';
import useViewPort from '@/utils/useViewport';

import { NavLink } from '../Link/Link';

const ArticleProductMetadata = ({
  article,
  inline = false,
}: {
  article: ArticleWithRelationsFromRequest | ArticleWithRelationsFromShipment;
  inline?: boolean;
}) => {
  const hasSku = !!article.product;
  const hasSerial = !!(article && 'uniqueArticle' in article && article.uniqueArticle !== null);
  const hasRef = !!article?.product?.data.reference;

  const sku = hasSku ? (
    <Paragraph size="200" color="secondary">
      <Trans id="article.product.external-id.short">SKU:</Trans>{' '}
      <span>{article.product?.externalId}</span>
    </Paragraph>
  ) : null;

  const serial = hasSerial ? (
    <Paragraph size="200" color="secondary">
      <Trans id="article.serial-number">Serial number:</Trans>{' '}
      <span>{article.uniqueArticle!.serialNumber}</span>
    </Paragraph>
  ) : null;

  const reference = hasRef ? (
    <Paragraph size="200" color="secondary">
      <Trans id="article.reference.short">Ref:</Trans>{' '}
      <span>{article.product!.data.reference}</span>
    </Paragraph>
  ) : null;

  if (inline) {
    const items = [sku, serial, reference].filter((item) => !!item);

    if (items.length === 0) {
      return null;
    }

    return (
      <Stack row gap="0.25rem" flexWrap="wrap" alignItems="center">
        {items.map((item, index) => (
          <Stack key={index} row gap="0.25rem" alignItems="center" flexWrap="nowrap">
            {index > 0 && (
              <Paragraph size="200" color="secondary">
                <span>•</span>
              </Paragraph>
            )}
            {item}
          </Stack>
        ))}
      </Stack>
    );
  }

  return (
    <>
      {sku}
      {serial}
      {reference}
    </>
  );
};

const ArticleProductMetadataLine2 = ({
  article,
  showRetailPrice,
}: {
  article: ArticleWithRelationsFromRequest | ArticleWithRelationsFromShipment;
  showRetailPrice?: boolean;
}) => {
  const { _db } = useDatabaseTranslation();

  const color = article?.color ? <span>{_db(article.color)}</span> : null;
  const size = article?.size ? <span>{_db(article.size)}</span> : null;
  const retailPrice =
    showRetailPrice && article.product?.data.purchaseValue ? (
      <>
        <Trans id="article.retail-price">Retail price:</Trans>{' '}
        <span>
          {formatCurrency(
            article.product.data.purchaseValue.amount,
            article.product.data.purchaseValue.currency
          )}
        </span>
      </>
    ) : null;

  const items = [color, size, retailPrice].filter((item) => !!item);

  if (items.length === 0) {
    return null;
  }

  return (
    <Stack row gap="0.25rem" flexWrap="wrap" alignItems="center">
      {items.map((item, index) => (
        <Stack key={index} row gap="0.25rem" alignItems="center" flexWrap="nowrap">
          {index > 0 && (
            <Paragraph size="200" color="secondary">
              <span>•</span>
            </Paragraph>
          )}
          <Paragraph size="200" color="secondary">
            {item}
          </Paragraph>
        </Stack>
      ))}
    </Stack>
  );
};

const ArticleNameCell = ({
  id,
  article,
  badge,
  noLink,
  photoSize,
  showWarranty = false,
  inlineSerialAndRef = false,
  showRetailPrice = false,
}: {
  id?: string;
  article: ArticleWithRelationsFromRequest | ArticleWithRelationsFromShipment;
  badge?: ReactNode;
  noLink?: boolean;
  photoSize?: PhotoCardProps['size'];
  showWarranty?: boolean;
  inlineSerialAndRef?: boolean;
  showRetailPrice?: boolean;
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const name = useArticleName({ article });

  const { request, view: requestView } = useRequestContext();
  const { shipment } = useContext(ShipmentArticlesContext);

  const isMissingCustomsInfo =
    article &&
    (request
      ? 'areCustomsExpected' in article &&
        article.areCustomsExpected &&
        isMissingCustomsData(article, requestView.customs.type)
      : shipment
        ? shipment.areCustomsExpected &&
          isMissingCustomsData(article, shipment.organization.config.customs?.type ?? 'fields')
        : false);

  const photoLabel = t({
    id: 'article.photo.label',
    message: `Photo of ${name}`,
  });
  const nameWithIcon = (
    <Stack
      row
      gap="4px"
      alignItems="center"
      flexWrap="nowrap"
      className={cn({
        'text-warning': request && isMissingCustomsInfo,
        'text-danger': shipment && isMissingCustomsInfo,
      })}
      style={{ overflow: 'hidden' }}
    >
      {request && isMissingCustomsInfo && (
        <Tooltip
          content={t({
            id: 'article-name-cell.missing-customs.tooltip',
            message: 'Missing customs information',
          })}
        >
          <Button variant="style-less">
            <IconPlace warning className="text-warning" style={{ fontSize: '1.25rem' }} />
          </Button>
        </Tooltip>
      )}
      {shipment && isMissingCustomsInfo && (
        <IconError className="text-danger" style={{ fontSize: '1.25rem' }} />
      )}
      <Paragraph weight="medium" ellipsis title={name}>
        {name}
      </Paragraph>
    </Stack>
  );

  return (
    <Stack
      row
      gap={isMobile ? '0.5rem' : '0.75rem'}
      alignItems="center"
      flexWrap="nowrap"
      style={{ minWidth: 0 }}
    >
      <PhotoCard
        url={getArticleDisplayPhoto(article)?.url}
        name={photoLabel}
        alt={photoLabel}
        size={photoSize ?? (isMobile ? 'small' : 'medium')}
        hideMoreIcon
      />

      <div style={{ overflow: 'hidden', flex: 1 }}>
        <Stack row alignItems="center" gap="0.5rem" flexWrap="nowrap">
          {id && article && !noLink ? (
            <NavLink
              to={`/requests/${article.requestId ?? article.archivedRequestId}/articles/${id}`}
              className="reset-link"
            >
              {nameWithIcon}
            </NavLink>
          ) : (
            nameWithIcon
          )}
          {badge}
        </Stack>

        <ArticleProductMetadata article={article} inline={inlineSerialAndRef} />
        <ArticleProductMetadataLine2 article={article} showRetailPrice={showRetailPrice} />

        {showWarranty && <ArticleWarranty article={article} />}
      </div>
    </Stack>
  );
};

const ArticleWarranty = ({
  article,
}: {
  article: ArticleWithRelationsFromRequest | ArticleWithRelationsFromShipment;
}) => {
  const noWarrantyInfo =
    !article.activeWarrantyType &&
    !article.supplierWarrantyExpirationDate &&
    !article.distributorWarrantyExpirationDate;

  if (noWarrantyInfo) {
    return null;
  }

  if (article.activeWarrantyType === 'supplier' && article.supplierWarrantyExpirationDate) {
    const {
      activeWarrantyType,
      supplierWarrantyExpirationDate,
      hasDistributorWarrantyExtension,
      distributorWarrantyExpirationDate,
    } = article;

    // For some reason, Typescript can't infer article type from the "if" condition, so I have to destructure all properties
    const date = computeWarrantyExpirationDate({
      activeWarrantyType,
      supplierWarrantyExpirationDate,
      hasDistributorWarrantyExtension,
      distributorWarrantyExpirationDate,
    });

    return (
      <Stack row gap="0.25rem" alignItems="center">
        <IconValidation className="color-primary-800" />
        <InlineText color="secondary" size="200">
          <Trans id="article-name-cell.warranty-applicable.with-expiration-date">
            Warranty applicable until {date}
          </Trans>
        </InlineText>
      </Stack>
    );
  }

  if (article.activeWarrantyType === 'distributor' && article.distributorWarrantyExpirationDate) {
    const { activeWarrantyType, distributorWarrantyExpirationDate } = article;

    // For some reason, Typescript can't infer article type from the "if" condition, so I have to destructure all properties
    const date = computeWarrantyExpirationDate({
      activeWarrantyType,
      distributorWarrantyExpirationDate,
    });

    return (
      <Stack row gap="0.25rem" alignItems="center">
        <IconValidation className="color-primary-800" />
        <InlineText color="secondary" size="200">
          <Trans id="article-name-cell.warranty-applicable.with-expiration-date">
            Warranty applicable until {date}
          </Trans>
        </InlineText>
      </Stack>
    );
  }

  if (article.activeWarrantyType === 'override') {
    const label =
      article.warrantyOverrideReason === 'commercial-gesture'
        ? t({
            id: 'article-name-cell.warranty-applicable.pending-commercial-gesture',
            message: 'Commercial gesture pending approval',
          })
        : t({
            id: 'article-name-cell.warranty-applicable',
            message: 'Warranty applicable',
          });

    return (
      <Stack row gap="0.25rem" alignItems="center">
        <IconValidation className="color-primary-800" />
        <InlineText color="secondary" size="200">
          {label}
        </InlineText>
      </Stack>
    );
  }

  return (
    <Stack row gap="0.25rem" alignItems="center">
      <IconRefusal className="text-danger" />
      <InlineText color="secondary" size="200">
        <Trans id="article-name-cell.warranty-not-applicable">Out of warranty</Trans>
      </InlineText>
    </Stack>
  );
};

export default ArticleNameCell;
